import { Typenames } from '@varo/graphql-schema-types';
import { Stack, useRouter } from 'expo-router';
import { V2AContext } from 'features/v2a/sender/provider/V2AContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const TransferSuccess = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_SAVINGS,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHARGE_CARD,
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const [showV2APromoScreen, setShowV2APromoScreen] = useState(false);
  const { confirmationData, setSelectedRecipient, selectedRecipient } =
    useContext(V2AContext);

  const isFirstTime = confirmationData?.isFirstTime;

  const isExternal =
    selectedRecipient?.__typename === Typenames.V2AExternalRecipient ||
    selectedRecipient?.__typename === undefined;

  //reset selectedRecipient when TransferSuccess unmounts
  useEffect(() => {
    return () => {
      setSelectedRecipient(undefined);
    };
  }, [setSelectedRecipient]);

  const navigateToNextScreen = () => {
    router.replace('/move-money/v2a/transfer-history');
  };

  const getContactType = (): string => {
    if (selectedRecipient?.email) {
      return 'email';
    } else if (selectedRecipient?.phoneNumber) {
      return 'phone';
    }
    return '';
  };

  const amount = confirmationData?.amount?.replace(/^\$/, '') || '0';

  const logV2aTransferSubmissionSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'V2aTransferSubmissionSuccessful',
    customAttributes: {
      amount: amount,
      is_first: isFirstTime ? isFirstTime.toString() : 'false',
      receiver_id: selectedRecipient?.id ?? '',
      receiver_type: isExternal ? 'external' : 'varo_customer',
      contact_type: getContactType(),
    },
  });

  const logV2aReceivedEvent = usePressableAnalytics({
    analyticsId: 'V2aTransferReceived',
    customAttributes: {
      receiver_id: selectedRecipient?.id ?? '',
      receiver_type: isExternal ? 'external' : 'varo_customer',
    },
  });

  const logV2aTransferUnsuccessfulEvent = usePressableAnalytics({
    analyticsId: 'V2aTransferUnsuccessful',
    customAttributes: {
      receiver_id: selectedRecipient?.id ?? '',
      receiver_type: isExternal ? 'external' : 'varo_customer',
      contact_type: getContactType(),
      error_name: confirmationData?.errorTitle ?? '',
      error_code: confirmationData?.errorCode ?? '',
    },
  });

  useEffect(() => {
    if (!showV2APromoScreen) {
      if (!confirmationData?.errorTitle) {
        logV2aTransferSubmissionSuccessfulEvent();
      } else {
        logV2aTransferUnsuccessfulEvent();
      }
    }
  }, [
    confirmationData?.errorTitle,
    logV2aTransferSubmissionSuccessfulEvent,
    logV2aTransferUnsuccessfulEvent,
    showV2APromoScreen,
  ]);

  const logGV2aHeadsUpEvent = usePressableAnalytics({
    analyticsId: 'V2aHeadsUp',
  });

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      {confirmationData?.errorTitle ? (
        <View style={styles.overlayContainer}>
          <ErrorScreen
            screenType={ErrorScreenType.Error}
            messageHeadline={confirmationData.errorTitle}
            messageBody={confirmationData.errorMessage}
            screenAnalyticsEventKey="transferUnsuccessfulScreen"
            primaryButton={{
              title: translatedText('promoScreen.ctaTitle'),
              onPress: () => {
                setSelectedRecipient(undefined);
                router.replace('/move-money/v2a/transfer');
              },
            }}
          />
        </View>
      ) : null}
      <SuccessScreen
        primaryButton={{
          label: showV2APromoScreen
            ? translatedText('promoScreen.ctaTitle')
            : (confirmationData?.ctaTitle ??
              translatedText('transferSuccess.ctaTitle')),
          onPress: () => {
            setSelectedRecipient(undefined);
            logV2aReceivedEvent();
            if (!showV2APromoScreen) {
              if (isExternal) {
                navigateToNextScreen();
              } else {
                setShowV2APromoScreen(true);
              }
            } else {
              logGV2aHeadsUpEvent();
              navigateToNextScreen();
            }
          },
        }}
        visual={showV2APromoScreen ? 'SEND_MONEY_A' : 'PLANE_A_ANIMATED'}
        screenAnalyticsEventKey={
          showV2APromoScreen ? 'promoScreen' : 'transferSuccessScreen'
        }
        titleText={
          showV2APromoScreen
            ? translatedText('promoScreen.title')
            : (confirmationData?.headerTitle as string)
        }
        content={
          <View>
            <BodyBaseText style={styles.contentText}>
              {showV2APromoScreen
                ? translatedText('promoScreen.body')
                : confirmationData?.successMessage}
            </BodyBaseText>
            {!showV2APromoScreen && confirmationData?.educationMessage ? (
              <BodyXSText style={styles.contentText}>
                {confirmationData.educationMessage}
              </BodyXSText>
            ) : null}
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentText: {
    marginBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'center',
  },
  overlayContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    height: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing.m,
    position: 'absolute',
    width: '100%',
    zIndex: 2,
  },
});

export default TransferSuccess;
